/**
 * エラー監視サービスの初期化
 * アプリケーション起動時に一度だけ実行される
 */

import { errorMonitoring } from './error-monitoring';

/**
 * エラー監視サービスを初期化
 */
export const initializeErrorMonitoring = (): void => {
  // 環境変数から設定を取得（ブラウザ環境対応）
  const config: Record<string, unknown> = {
    environment: (typeof process !== 'undefined' ? process.env.NODE_ENV : 'development') || 'development',
    release: (typeof process !== 'undefined' ? process.env.REACT_APP_VERSION : 'unknown') || 'unknown',
    enablePerformanceMonitoring: (typeof process !== 'undefined' ? process.env.NODE_ENV : 'development') === 'production',
  };
  
  // DSNが存在する場合のみ追加
  if (typeof process !== 'undefined' && process.env.REACT_APP_SENTRY_DSN) {
    config.dsn = process.env.REACT_APP_SENTRY_DSN;
  }

  // エラー監視サービスを初期化
  errorMonitoring.initialize(config as Partial<import('./error-monitoring').ErrorMonitoringConfig>);

  // セッションIDを生成・設定
  const sessionId = generateSessionId();
  errorMonitoring.setSessionId(sessionId);

  // 初期パンくずリストを追加
  errorMonitoring.addBreadcrumb('Application started', 'lifecycle', 'info');

  // パフォーマンス監視の開始
  if (config.enablePerformanceMonitoring) {
    setupPerformanceMonitoring();
  }

  // グローバルエラーハンドラーの設定
  setupGlobalErrorHandlers();
};

/**
 * セッションIDを生成
 */
const generateSessionId = (): string => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substr(2, 9);
  return `session_${timestamp}_${randomPart}`;
};

/**
 * パフォーマンス監視の設定
 */
const setupPerformanceMonitoring = (): void => {
  // Core Web Vitals の監視
  if ('web-vital' in window || typeof window !== 'undefined') {
    // LCP (Largest Contentful Paint)
    new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        if (entry.entryType === 'largest-contentful-paint') {
          errorMonitoring.capturePerformance(
            'LCP',
            entry.startTime,
            { type: 'web-vital' }
          );
        }
      }
    }).observe({ entryTypes: ['largest-contentful-paint'] });

    // FID (First Input Delay) - 実際のユーザーインタラクション時に測定
    document.addEventListener('click', () => {
      new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          if (entry.entryType === 'first-input') {
            errorMonitoring.capturePerformance(
              'FID',
              (entry as PerformanceEventTiming).processingStart - entry.startTime,
              { type: 'web-vital' }
            );
          }
        }
      }).observe({ entryTypes: ['first-input'] });
    }, { once: true });

    // CLS (Cumulative Layout Shift)
    new PerformanceObserver((entryList) => {
      let clsScore = 0;
      for (const entry of entryList.getEntries()) {
        if (entry.entryType === 'layout-shift' && !(entry as PerformanceEntry & { hadRecentInput: boolean }).hadRecentInput) {
          clsScore += (entry as PerformanceEntry & { value: number }).value;
        }
      }
      if (clsScore > 0) {
        errorMonitoring.capturePerformance(
          'CLS',
          clsScore,
          { type: 'web-vital' }
        );
      }
    }).observe({ entryTypes: ['layout-shift'] });
  }

  // ページロード時間の監視
  window.addEventListener('load', () => {
    setTimeout(() => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        errorMonitoring.capturePerformance(
          'Page Load',
          navigation.loadEventEnd - (navigation.startTime || 0),
          { type: 'page-load' }
        );
      }
    }, 0);
  });
};

/**
 * グローバルエラーハンドラーの設定
 */
const setupGlobalErrorHandlers = (): void => {
  // 未捕捉のJavaScriptエラー
  window.addEventListener('error', (event) => {
    errorMonitoring.addBreadcrumb(
      `Global error: ${event.error?.message || event.message}`,
      'error',
      'error'
    );
  });

  // 未捕捉のPromise reject
  window.addEventListener('unhandledrejection', (event) => {
    errorMonitoring.addBreadcrumb(
      `Unhandled promise rejection: ${event.reason}`,
      'promise',
      'error'
    );
  });

  // リソース読み込みエラー
  window.addEventListener('error', (event) => {
    if (event.target !== window) {
      const target = event.target as HTMLElement;
      const srcAttr = 'src' in target ? (target as HTMLImageElement | HTMLScriptElement).src : '';
      const hrefAttr = 'href' in target ? (target as HTMLLinkElement).href : '';
      const resourceUrl = srcAttr || hrefAttr || 'unknown';
      
      errorMonitoring.addBreadcrumb(
        `Resource load error: ${target.tagName} ${resourceUrl}`,
        'resource',
        'error'
      );
    }
  }, true);
};

/**
 * ユーザー情報を設定（ログイン後などに呼び出し）
 */
export const setUserContext = (userId: string, metadata: Record<string, string> = {}): void => {
  errorMonitoring.setUserContext(userId, metadata);
};